import { useMemo, type ReactNode } from "react";
import type { TimelineItemWithCommentCount } from "@travel/shared";
import { TimelineItem } from "./TimelineItem";
import { DayHeader } from "./DayHeader";
import { NowMarker } from "./NowMarker";
import { JumpToNowButton } from "./JumpToNowButton";
import { useScrollToNow } from "../../hooks/useScrollToNow";
import { getLocalDateKey } from "../../utils/time";
import { classify } from "../../utils/status";
import { buildDirectionsUrl, getPlaceQuery } from "../../utils/maps";

interface Props {
  planId: string;
  items: TimelineItemWithCommentCount[];
  now: number;
  onItemClick: (item: TimelineItemWithCommentCount) => void;
  onCommentIconClick: (item: TimelineItemWithCommentCount) => void;
}

export function Timeline({ planId, items, now, onItemClick, onCommentIconClick }: Props) {
  const { sorted, dayGroups, nowAnchorId } = useMemo(() => {
    const sorted = [...items].sort((a, b) => Date.parse(a.startUtc) - Date.parse(b.startUtc));
    const dayGroups = new Map<string, TimelineItemWithCommentCount[]>();
    for (const it of sorted) {
      const key = getLocalDateKey(it.startUtc);
      const arr = dayGroups.get(key) ?? [];
      arr.push(it);
      dayGroups.set(key, arr);
    }
    let anchor: string | null = null;
    for (const it of sorted) {
      const status = classify(it.startUtc, it.endUtc, now);
      if (status === "current" || status === "future") {
        anchor = it.id;
        break;
      }
    }
    if (!anchor && sorted.length > 0) {
      anchor = sorted[sorted.length - 1]!.id;
    }
    return { sorted, dayGroups, nowAnchorId: anchor };
    // Anchor depends on `now` but we only want to scroll once per plan, so the scroll hook
    // keys off planId. The anchor recomputation here is fine — only the scroll is gated.
  }, [items, now]);

  useScrollToNow(nowAnchorId, planId);

  if (items.length === 0) {
    return <p className="text-center text-slate-500 py-12">등록된 일정이 없습니다.</p>;
  }

  const rows: ReactNode[] = [];
  let nowMarkerInserted = false;
  let prevItem: TimelineItemWithCommentCount | null = null;

  const dayKeys = Array.from(dayGroups.keys()).sort();
  for (const dayKey of dayKeys) {
    const dayItems = dayGroups.get(dayKey)!;
    const places = dayItems
      .filter((it) => !!it.googleMapLink)
      .map((it) => getPlaceQuery(it))
      .filter((p): p is string => !!p);
    const directionsUrl = buildDirectionsUrl(places);
    rows.push(
      <DayHeader
        key={`day-${dayKey}`}
        utcIso={dayItems[0]!.startUtc}
        directionsUrl={directionsUrl}
      />,
    );
    for (const it of dayItems) {
      const status = classify(it.startUtc, it.endUtc, now);

      if (!nowMarkerInserted) {
        const startMs = Date.parse(it.startUtc);
        const prevEndMs = prevItem ? Date.parse(prevItem.endUtc) : -Infinity;
        if (now >= prevEndMs && now < startMs) {
          rows.push(<NowMarker key={`now-${it.id}`} now={now} />);
          nowMarkerInserted = true;
        }
      }

      rows.push(
        <TimelineItem
          key={it.id}
          item={it}
          status={status}
          onClick={() => onItemClick(it)}
          onCommentIconClick={() => onCommentIconClick(it)}
        />,
      );
      prevItem = it;
    }
  }

  if (
    !nowMarkerInserted &&
    sorted.length > 0 &&
    classify(sorted[sorted.length - 1]!.startUtc, sorted[sorted.length - 1]!.endUtc, now) === "past"
  ) {
    rows.push(<NowMarker key="now-end" now={now} />);
  }

  return (
    <>
      <div className="space-y-2">{rows}</div>
      <JumpToNowButton anchorId={nowAnchorId} />
    </>
  );
}
